import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../api/client';
import {
  addTaskToInbox,
  fetchStatementIssues,
  patchStatementIssue,
} from '../api/taskInbox';
import MarkdownText from '../components/MarkdownText';
import OfficialSolutionsPanel from '../components/OfficialSolutionsPanel';
import ReadOnlyPythonCode from '../components/ReadOnlyPythonCode';
import StatementAttachmentLinks from '../components/StatementAttachmentLinks';
import TaskStatementMeta from '../components/TaskStatementMeta';
import { useAuth } from '../context/AuthContext';
import { taskNavRef } from '../utils/taskNav';

const SELF_REPORT_LABELS = {
  EASY: '++',
  OK_NO_PEEK: '+',
  OK_WITH_PEEK: '+-',
  OK_WITH_QUESTION: '+?',
  FAILED: '-',
  NO_IDEAS: '--',
  NOT_TRIED: '!',
};

function groupInboxBySource(items) {
  const groups = new Map();
  for (const item of items) {
    const key = item.task?.source_name || 'Без сборника';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(item);
  }
  return Array.from(groups.entries()).map(([sourceName, rows]) => ({ sourceName, rows }));
}

function groupIssuesBySource(tasks) {
  const groups = new Map();
  for (const task of tasks) {
    const key = task.source_name || 'Без сборника';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(task);
  }
  return Array.from(groups.entries()).map(([sourceName, rows]) => ({ sourceName, rows }));
}

function taskNumberLabel(task) {
  const num = String(task?.source_number || '').trim();
  return num || `#${task?.id}`;
}

function matchLabel(match) {
  const name = String(match?.source_name || '').trim() || 'Без сборника';
  const num = String(match?.source_number || '').trim() || `#${match?.id}`;
  return `${name} №${num}`;
}

export default function TeacherTaskInboxPage() {
  const { user } = useAuth();
  const isSiteAdmin = Boolean(user?.is_staff || user?.is_admin);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedIdParam = searchParams.get('id');
  const selectedIssueParam = searchParams.get('issue');

  const [items, setItems] = useState([]);
  const [issueTasks, setIssueTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(
    selectedIdParam ? Number(selectedIdParam) : null,
  );
  const [selectedIssueId, setSelectedIssueId] = useState(
    selectedIssueParam ? Number(selectedIssueParam) : null,
  );

  const [numberInput, setNumberInput] = useState('');
  const [addBusy, setAddBusy] = useState(false);
  const [addError, setAddError] = useState('');
  const [ambiguousMatches, setAmbiguousMatches] = useState([]);

  const [taskFull, setTaskFull] = useState(null);
  const [taskLoading, setTaskLoading] = useState(false);
  const [attemptsData, setAttemptsData] = useState(null);
  const [attemptsLoading, setAttemptsLoading] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState('');
  const [expandedAttemptId, setExpandedAttemptId] = useState(null);

  const loadInbox = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [inboxRes, issuesRes] = await Promise.all([
        api.get('/teacher/task-inbox/', { params: { status: 'open' } }),
        fetchStatementIssues(),
      ]);
      const list = Array.isArray(inboxRes.data?.results) ? inboxRes.data.results : [];
      const issues = Array.isArray(issuesRes?.results) ? issuesRes.results : [];
      setItems(list);
      setIssueTasks(issues);
      return { list, issues };
    } catch {
      setItems([]);
      setIssueTasks([]);
      setError('Не удалось загрузить очередь.');
      return { list: [], issues: [] };
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInbox();
  }, [loadInbox]);

  useEffect(() => {
    if (selectedIdParam) {
      const n = Number(selectedIdParam);
      if (Number.isFinite(n)) {
        setSelectedId(n);
        setSelectedIssueId(null);
      }
    } else if (selectedIssueParam) {
      const n = Number(selectedIssueParam);
      if (Number.isFinite(n)) {
        setSelectedIssueId(n);
        setSelectedId(null);
      }
    }
  }, [selectedIdParam, selectedIssueParam]);

  const selectedItem = useMemo(
    () => items.find((x) => x.id === selectedId) || null,
    [items, selectedId],
  );

  const selectedIssueTask = useMemo(
    () => issueTasks.find((t) => t.id === selectedIssueId) || null,
    [issueTasks, selectedIssueId],
  );

  const selectInboxItem = useCallback((id) => {
    setSelectedId(id);
    setSelectedIssueId(null);
    const next = new URLSearchParams(searchParams);
    if (id) next.set('id', String(id));
    else next.delete('id');
    next.delete('issue');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const selectIssueTask = useCallback((taskId) => {
    setSelectedIssueId(taskId);
    setSelectedId(null);
    const next = new URLSearchParams(searchParams);
    if (taskId) next.set('issue', String(taskId));
    else next.delete('issue');
    next.delete('id');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const activeTaskMeta = selectedItem?.task || selectedIssueTask || null;

  useEffect(() => {
    if (!activeTaskMeta?.id) {
      setTaskFull(null);
      setAttemptsData(null);
      setNoteDraft('');
      return undefined;
    }
    setNoteDraft(selectedItem?.note || '');
    const taskId = activeTaskMeta.id;
    const ref = activeTaskMeta.url_ref || String(taskId);
    let cancelled = false;

    setTaskLoading(true);
    setAttemptsLoading(true);
    setActionError('');
    setExpandedAttemptId(null);

    api
      .get(`/tasks/editor/${ref}/`)
      .then((res) => {
        if (!cancelled) setTaskFull(res.data);
      })
      .catch(() => {
        if (!cancelled) setTaskFull(null);
      })
      .finally(() => {
        if (!cancelled) setTaskLoading(false);
      });

    api
      .get(`/teacher/tasks/${taskId}/attempts/`)
      .then((res) => {
        if (!cancelled) setAttemptsData(res.data);
      })
      .catch(() => {
        if (!cancelled) setAttemptsData(null);
      })
      .finally(() => {
        if (!cancelled) setAttemptsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    selectedItem?.id,
    selectedItem?.note,
    selectedIssueTask?.id,
    activeTaskMeta?.id,
    activeTaskMeta?.url_ref,
  ]);

  const inboxGroups = useMemo(() => groupInboxBySource(items), [items]);
  const issueGroups = useMemo(() => groupIssuesBySource(issueTasks), [issueTasks]);

  async function enqueueByTaskId(taskId) {
    setAddBusy(true);
    setAddError('');
    try {
      const item = await addTaskToInbox({ taskId });
      setAmbiguousMatches([]);
      setNumberInput('');
      const { list } = await loadInbox();
      if (item?.id) selectInboxItem(item.id);
      else if (list.length) selectInboxItem(list[0].id);
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;
      if (status === 409 && Array.isArray(data?.matches)) {
        setAmbiguousMatches(data.matches);
        setAddError(data.detail || 'Несколько задач с таким номером.');
      } else {
        setAmbiguousMatches([]);
        setAddError(data?.detail || 'Не удалось добавить в очередь.');
      }
    } finally {
      setAddBusy(false);
    }
  }

  async function enqueueByNumber(ev) {
    ev?.preventDefault?.();
    const number = numberInput.trim();
    if (!number || addBusy) return;
    setAddBusy(true);
    setAddError('');
    setAmbiguousMatches([]);
    try {
      const item = await addTaskToInbox({ number });
      setNumberInput('');
      const { list } = await loadInbox();
      if (item?.id) selectInboxItem(item.id);
      else if (list.length) selectInboxItem(list[0].id);
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;
      if (status === 409 && Array.isArray(data?.matches)) {
        setAmbiguousMatches(data.matches);
        setAddError(data.detail || 'Несколько задач с таким номером.');
      } else {
        setAddError(data?.detail || 'Не удалось добавить в очередь.');
      }
    } finally {
      setAddBusy(false);
    }
  }

  async function saveNote() {
    if (!selectedItem) return;
    setNoteSaving(true);
    setActionError('');
    try {
      const res = await api.patch(`/teacher/task-inbox/${selectedItem.id}/`, { note: noteDraft });
      setItems((prev) => prev.map((x) => (x.id === selectedItem.id ? { ...x, ...res.data } : x)));
    } catch {
      setActionError('Не удалось сохранить заметку.');
    } finally {
      setNoteSaving(false);
    }
  }

  async function markDone() {
    if (!selectedItem) return;
    setActionBusy(true);
    setActionError('');
    try {
      await api.patch(`/teacher/task-inbox/${selectedItem.id}/`, { status: 'done' });
      const { list } = await loadInbox();
      if (list.length) selectInboxItem(list[0].id);
      else selectInboxItem(null);
    } catch {
      setActionError('Не удалось снять с очереди.');
    } finally {
      setActionBusy(false);
    }
  }

  async function clearStatementIssue() {
    const taskId = selectedIssueTask?.id;
    if (!taskId) return;
    setActionBusy(true);
    setActionError('');
    try {
      await patchStatementIssue(taskId, false);
      const { issues } = await loadInbox();
      if (issues.length) selectIssueTask(issues[0].id);
      else selectIssueTask(null);
    } catch {
      setActionError('Не удалось снять пометку.');
    } finally {
      setActionBusy(false);
    }
  }

  async function archiveTask() {
    if (!taskFull?.id && !activeTaskMeta?.id) return;
    const ref = taskNavRef(taskFull || activeTaskMeta);
    if (!window.confirm('Скрыть задачу из каталога (статус «архив»)?')) return;
    setActionBusy(true);
    setActionError('');
    try {
      await api.patch(`/tasks/editor/${ref}/`, { status: 'archived' });
      if (taskFull) setTaskFull({ ...taskFull, status: 'archived' });
      if (selectedItem) {
        setItems((prev) => prev.map((x) => (
          x.id === selectedItem.id
            ? { ...x, task: { ...x.task, status: 'archived' } }
            : x
        )));
      }
    } catch {
      setActionError('Не удалось архивировать задачу.');
    } finally {
      setActionBusy(false);
    }
  }

  async function deleteTask() {
    const taskId = activeTaskMeta?.id;
    if (!taskId) return;
    if (!window.confirm('Удалить задачу безвозвратно? Это нельзя отменить.')) return;
    setActionBusy(true);
    setActionError('');
    try {
      await api.post('/tasks/editor/bulk-delete/', { task_ids: [taskId] });
      const { list, issues } = await loadInbox();
      if (selectedItem) {
        if (list.length) selectInboxItem(list[0].id);
        else selectInboxItem(null);
      } else if (selectedIssueTask) {
        if (issues.length) selectIssueTask(issues[0].id);
        else selectIssueTask(null);
      }
      setTaskFull(null);
      setAttemptsData(null);
    } catch {
      setActionError('Не удалось удалить задачу.');
    } finally {
      setActionBusy(false);
    }
  }

  const taskRef = taskFull ? taskNavRef(taskFull) : (activeTaskMeta?.url_ref || (activeTaskMeta?.id ? String(activeTaskMeta.id) : null));
  const expectedAnswer = String(taskFull?.expected_answer ?? '').trim();
  const viewingInbox = Boolean(selectedItem);
  const viewingIssue = Boolean(selectedIssueTask) && !selectedItem;

  return (
    <div className="teacher-task-inbox-page">
      <header className="page-header">
        <h1>Очередь задач</h1>
        <p className="page-subtitle text-secondary">
          Моя очередь и отдельно задачи с проблемой в условии.
        </p>
      </header>

      <form className="task-inbox-add-form" onSubmit={enqueueByNumber}>
        <label className="task-inbox-add-label" htmlFor="task-inbox-number">
          Добавить по номеру
        </label>
        <div className="task-inbox-add-row">
          <input
            id="task-inbox-number"
            type="text"
            className="form-input"
            value={numberInput}
            onChange={(e) => {
              setNumberInput(e.target.value);
              if (ambiguousMatches.length) setAmbiguousMatches([]);
              if (addError) setAddError('');
            }}
            placeholder="Номер в сборнике или id сайта"
            disabled={addBusy}
            autoComplete="off"
          />
          <button type="submit" className="btn btn-primary btn-sm" disabled={addBusy || !numberInput.trim()}>
            {addBusy ? '…' : 'В очередь'}
          </button>
        </div>
        {addError ? <p className="alert alert-error task-inbox-add-error">{addError}</p> : null}
        {ambiguousMatches.length > 0 ? (
          <div className="task-inbox-ambiguous">
            <p className="text-secondary">Выберите задачу:</p>
            <ul className="task-inbox-number-list">
              {ambiguousMatches.map((match) => (
                <li key={match.id}>
                  <button
                    type="button"
                    className="task-inbox-number-btn"
                    disabled={addBusy}
                    onClick={() => enqueueByTaskId(match.id)}
                    title={match.title || undefined}
                  >
                    {matchLabel(match)}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </form>

      {error ? <div className="alert alert-error">{error}</div> : null}
      {actionError ? <div className="alert alert-error">{actionError}</div> : null}

      <div className="task-inbox-layout">
        <aside className="task-inbox-list-panel">
          <div className="task-inbox-list-head">
            <h2 className="teacher-tools-section-title">Списки</h2>
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={loadInbox}
              disabled={loading}
            >
              Обновить
            </button>
          </div>
          {loading ? (
            <p className="text-secondary">Загрузка…</p>
          ) : (
            <>
              <section className="task-inbox-list-section" aria-label="Моя очередь">
                <h3 className="task-inbox-list-section-title">
                  Моя очередь
                  <span className="text-secondary"> ({items.length})</span>
                </h3>
                {items.length === 0 ? (
                  <p className="text-secondary">Пусто.</p>
                ) : (
                  <div className="teacher-tools-issues">
                    {inboxGroups.map((group) => (
                      <div key={`inbox-${group.sourceName}`} className="teacher-tools-issue-group">
                        <h4 className="teacher-tools-issue-source">{group.sourceName}</h4>
                        <ul className="task-inbox-number-list">
                          {group.rows.map((item) => (
                            <li key={item.id}>
                              <button
                                type="button"
                                className={`task-inbox-number-btn${selectedId === item.id ? ' is-active' : ''}`}
                                onClick={() => selectInboxItem(item.id)}
                              >
                                {taskNumberLabel(item.task)}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <section className="task-inbox-list-section" aria-label="Проблемы с условием">
                <h3 className="task-inbox-list-section-title">
                  Проблемы с условием
                  <span className="text-secondary"> ({issueTasks.length})</span>
                </h3>
                {issueTasks.length === 0 ? (
                  <p className="text-secondary">Пусто.</p>
                ) : (
                  <div className="teacher-tools-issues">
                    {issueGroups.map((group) => (
                      <div key={`issue-${group.sourceName}`} className="teacher-tools-issue-group">
                        <h4 className="teacher-tools-issue-source">{group.sourceName}</h4>
                        <ul className="task-inbox-number-list">
                          {group.rows.map((task) => (
                            <li key={task.id}>
                              <button
                                type="button"
                                className={`task-inbox-number-btn${selectedIssueId === task.id ? ' is-active' : ''}`}
                                onClick={() => selectIssueTask(task.id)}
                              >
                                {taskNumberLabel(task)}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </>
          )}
        </aside>

        <main className="task-inbox-detail-panel">
          {!activeTaskMeta ? (
            <p className="text-secondary">Выберите номер слева или добавьте задачу по номеру.</p>
          ) : (
            <>
              <div className="task-inbox-detail-head">
                <div>
                  <h2>{taskFull?.title || activeTaskMeta?.title || taskNumberLabel(activeTaskMeta)}</h2>
                  <p className="text-secondary task-inbox-meta-line">
                    {activeTaskMeta?.source_name
                      ? `${activeTaskMeta.source_name} · №${activeTaskMeta.source_number || activeTaskMeta.id}`
                      : `id ${activeTaskMeta?.id}`}
                    {viewingInbox && selectedItem?.student_username
                      ? ` · контекст: ${selectedItem.student_username}`
                      : ''}
                    {viewingIssue ? ' · проблемы с условием' : ''}
                    {` · статус задачи: ${taskFull?.status || activeTaskMeta?.status || '—'}`}
                  </p>
                </div>
                <div className="task-inbox-actions">
                  {taskRef ? (
                    <>
                      <Link className="btn btn-outline btn-sm" to={`/work/${taskRef}`}>
                        Как у ученика
                      </Link>
                      <Link className="btn btn-outline btn-sm" to={`/teacher/tasks?id=${activeTaskMeta.id}`}>
                        Редактор
                      </Link>
                      <Link
                        className="btn btn-outline btn-sm"
                        to={`/teacher/ege-families?q=${encodeURIComponent(activeTaskMeta.source_number || String(activeTaskMeta.id))}`}
                      >
                        Семьи / карта
                      </Link>
                    </>
                  ) : null}
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    disabled={actionBusy}
                    onClick={archiveTask}
                  >
                    Скрыть (архив)
                  </button>
                  {isSiteAdmin ? (
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      disabled={actionBusy}
                      onClick={deleteTask}
                    >
                      Удалить
                    </button>
                  ) : null}
                  {viewingInbox ? (
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={actionBusy}
                      onClick={markDone}
                    >
                      Готово
                    </button>
                  ) : null}
                  {viewingIssue ? (
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={actionBusy}
                      onClick={clearStatementIssue}
                    >
                      Снять пометку
                    </button>
                  ) : null}
                </div>
              </div>

              {viewingInbox ? (
                <section className="task-inbox-section">
                  <h3>Заметка</h3>
                  <textarea
                    className="form-input"
                    rows={3}
                    value={noteDraft}
                    onChange={(e) => setNoteDraft(e.target.value)}
                    placeholder="Что проверить: ответ, тесты, формулировка…"
                  />
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    style={{ marginTop: '0.5rem' }}
                    disabled={noteSaving}
                    onClick={saveNote}
                  >
                    {noteSaving ? 'Сохранение…' : 'Сохранить заметку'}
                  </button>
                </section>
              ) : null}

              <section className="task-inbox-section">
                <h3>Условие</h3>
                {taskLoading ? (
                  <p className="text-secondary">Загрузка условия…</p>
                ) : taskFull ? (
                  <div className="task-statement-block">
                    <TaskStatementMeta
                      source_name={taskFull.source_name}
                      source_number={taskFull.source_number}
                      source_author={taskFull.source_author}
                      year={taskFull.year}
                    />
                    <StatementAttachmentLinks attachments={taskFull.statement_attachments} variant="work" />
                    <MarkdownText className="statement-text" text={taskFull.statement || ''} />
                  </div>
                ) : (
                  <p className="text-secondary">Не удалось загрузить условие.</p>
                )}
              </section>

              <section className="task-inbox-section">
                <h3>Эталон</h3>
                {taskLoading ? (
                  <p className="text-secondary">Загрузка…</p>
                ) : taskFull ? (
                  <div className="task-inbox-etalon">
                    {expectedAnswer ? (
                      <p>
                        <strong>Ожидаемый ответ:</strong>{' '}
                        <code className="task-inbox-answer">{expectedAnswer}</code>
                      </p>
                    ) : (
                      <p className="text-secondary">Ожидаемый ответ не задан.</p>
                    )}
                    <OfficialSolutionsPanel task={taskFull} />
                  </div>
                ) : null}
              </section>

              <section className="task-inbox-section">
                <h3>Попытки учеников</h3>
                {attemptsLoading ? (
                  <p className="text-secondary">Загрузка попыток…</p>
                ) : !attemptsData?.students?.length ? (
                  <p className="text-secondary">Попыток пока нет.</p>
                ) : (
                  <div className="task-inbox-attempts">
                    {attemptsData.truncated ? (
                      <p className="text-secondary">Показаны последние {attemptsData.count} попыток.</p>
                    ) : null}
                    {attemptsData.students.map((group) => (
                      <div key={group.student_id} className="task-inbox-student-group">
                        <h4>
                          {group.student_display_name || group.student_username || `Ученик #${group.student_id}`}
                          <span className="text-secondary"> · {group.attempts.length}</span>
                        </h4>
                        <ul className="task-inbox-attempt-list">
                          {group.attempts.map((a) => (
                            <li key={a.id}>
                              <button
                                type="button"
                                className="task-inbox-attempt-toggle"
                                onClick={() => setExpandedAttemptId((cur) => (cur === a.id ? null : a.id))}
                              >
                                {a.created_at
                                  ? new Date(a.created_at).toLocaleString('ru-RU')
                                  : `#${a.id}`}
                                {' — '}
                                {SELF_REPORT_LABELS[a.self_report_status] || a.self_report_status || '!'}
                              </button>
                              {expandedAttemptId === a.id ? (
                                <div className="task-inbox-attempt-body">
                                  {a.self_comment ? (
                                    <p><strong>Комментарий ученика:</strong> {a.self_comment}</p>
                                  ) : null}
                                  {a.teacher_comment ? (
                                    <p><strong>Комментарий преподавателя:</strong> {a.teacher_comment}</p>
                                  ) : null}
                                  {a.code_snapshot?.trim() ? (
                                    <ReadOnlyPythonCode value={a.code_snapshot} />
                                  ) : (
                                    <p className="text-secondary">(код пуст)</p>
                                  )}
                                  <Link
                                    className="btn btn-outline btn-xs"
                                    to={`/teacher/students/${group.student_id}`}
                                  >
                                    Карточка ученика
                                  </Link>
                                </div>
                              ) : null}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
